import React, { useEffect, useState } from "react";
import { supabase } from "./supabase/auth";
import { useTranslation } from 'react-i18next';

interface Props {
  onNavigate: (view: string) => void;
}

/* ── Small SVG icons for KPI cards ── */
const BeakerSmall = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4.5 3h15" />
    <path d="M6 3v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V3" />
    <path d="M6 14h12" />
  </svg>
);

const FlaskSmall = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3h6" />
    <path d="M10 3v7.4a2 2 0 0 1-.6 1.4L4 17.2a2 2 0 0 0-.6 1.4V20a1 1 0 0 0 1 1h15.2a1 1 0 0 0 1-1v-1.4a2 2 0 0 0-.6-1.4L14.6 11.8a2 2 0 0 1-.6-1.4V3" />
  </svg>
);

const LinkSmall = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#06b6d4" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const cardStyle: React.CSSProperties = {
  background: "rgba(15,10,30,0.95)",
  border: "1px solid rgba(139,92,246,0.3)",
  borderRadius: "12px",
  padding: "28px 24px",
  flex: "1 1 200px",
  minWidth: "180px",
  cursor: "pointer",
  transition: "border-color 0.2s, transform 0.2s",
  textAlign: "center",
};

export default function DatabaseHome({ onNavigate }: Props) {
  const { t } = useTranslation();
  const [ingredientCount, setIngredientCount] = useState<number | null>(null);
  const [formulaCount, setFormulaCount] = useState<number | null>(null);
  const [componentCount, setComponentCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchCounts = async () => {
      const [ingRes, formRes, compRes] = await Promise.all([
        supabase.from("ingredients").select("id", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("formulas").select("formula_code", { count: "exact", head: true }).eq("is_active", true),
        supabase.from("formula_components").select("formula_code", { count: "exact", head: true }),
      ]);
      setIngredientCount(ingRes.count ?? 0);
      setFormulaCount(formRes.count ?? 0);
      setComponentCount(compRes.count ?? 0);
    };
    fetchCounts();
  }, []);

  const kpis = [
    { label: t('database.ingredients'), count: ingredientCount, icon: <BeakerSmall />, view: "ingredients", color: "#a78bfa" },
    { label: t('database.formulas'), count: formulaCount, icon: <FlaskSmall />, view: "formulas", color: "#ec4899" },
    { label: t('database.components'), count: componentCount, icon: <LinkSmall />, view: null, color: "#06b6d4" },
  ];

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "40px" }}>
        {kpis.map((kpi) => (
          <div
            key={kpi.label}
            style={{
              ...cardStyle,
              cursor: kpi.view ? "pointer" : "default",
            }}
            onClick={() => kpi.view && onNavigate(kpi.view)}
            onMouseEnter={(e) => {
              if (kpi.view) {
                e.currentTarget.style.borderColor = kpi.color;
                e.currentTarget.style.transform = "translateY(-3px)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(139,92,246,0.3)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <div style={{ marginBottom: "12px" }}>{kpi.icon}</div>
            <div
              style={{
                fontSize: "36px",
                fontWeight: 800,
                color: kpi.color,
                lineHeight: 1,
                marginBottom: "8px",
              }}
            >
              {kpi.count === null ? "-" : kpi.count}
            </div>
            <div style={{ fontSize: "13px", color: "#94a3b8", fontWeight: 500 }}>
              {kpi.label}
            </div>
          </div>
        ))}
      </div>

      {/* Recent Updates placeholder */}
      <div
        style={{
          background: "rgba(15,10,30,0.95)",
          border: "1px solid rgba(139,92,246,0.15)",
          borderRadius: "12px",
          padding: "24px",
        }}
      >
        <h3
          style={{
            margin: "0 0 12px 0",
            fontSize: "15px",
            fontWeight: 600,
            color: "#c4b5fd",
          }}
        >
          {t('database.recentUpdates')}
        </h3>
        <p style={{ margin: 0, color: "#4a4560", fontSize: "14px" }}>
          {t('database.comingPhase2')}
        </p>
      </div>
    </div>
  );
}
